import { DateTime } from "luxon";
import { semvApi } from "../../../../../../shared/services/axios/apis.service";
import { GOVERNMENT_PLAN_STATUS } from "../../types/status";
import { GovPlanSecretaryProjectRoadmapItemContract } from "../contracts/roadmap-item";

type IGovPlanSecretaryProjectRoadmapItem = {
    id: number;
    name: string;
    status: GOVERNMENT_PLAN_STATUS;
    description?: string;
    amount?: number;
    deadline: Date;
    observation?: string;
    priority: number;
    projectId: number;
    completedAt?: Date;
    canceledAt?: Date;
    createdAt: Date;
    updatedAt: Date;
};

export class GovPlanSecretaryProjectRoadmapItem {
    id!: number;

    name!: string;

    status!: GOVERNMENT_PLAN_STATUS;

    description?: string;

    amount?: number;

    deadline!: Date;

    observation?: string;

    priority!: number;

    projectId!: number;

    completedAt?: Date;

    canceledAt?: Date;

    createdAt!: Date;

    updatedAt!: Date;

    constructor(data: IGovPlanSecretaryProjectRoadmapItem) {
        Object.assign(this, data);
    }

    async toggle() {
        if (this.isCompleted()) {
            await semvApi.patch(
                `/government-plan/projects/${this.projectId}/items/${this.id}/pending`
            );

            this.status = GOVERNMENT_PLAN_STATUS.IN_PROGRESS;
        } else {
            await semvApi.patch(
                `/government-plan/projects/${this.projectId}/items/${this.id}/complete`
            );

            this.status = GOVERNMENT_PLAN_STATUS.COMPLETED;
        }

        return this;
    }

    static async create({
        projectId,
        ...payload
    }: {
        name: string;
        description: string;
        amount: number;
        observation?: string;
        deadline: string | Date;
        projectId: number;
    }) {
        await semvApi.post(
            `/government-plan/projects/${projectId}/items`,
            payload
        );
    }

    async update({
        ...payload
    }: {
        name: string;
        description: string;
        amount: number;
        observation?: string;
        deadline: string | Date;
    }) {
        await semvApi.patch(
            `/government-plan/projects/${this.projectId}/items/${this.id}`,
            payload
        );
    }

    isCompleted() {
        return [
            GOVERNMENT_PLAN_STATUS.COMPLETED,
            GOVERNMENT_PLAN_STATUS.COMPLETED_LATE,
        ].includes(this.status);
    }

    daysUntilDeadline() {
        const deadline = DateTime.fromJSDate(this.deadline);

        return Math.floor(deadline.diffNow("days").days);
    }

    nearDeadline() {
        const deadline = DateTime.fromJSDate(this.deadline);

        return (
            Math.floor(deadline.diffNow("days").days) <
            Math.floor(this.intervalBetweenCreationAndDeadline() / 2)
        );
    }

    isLate() {
        const deadline = DateTime.fromJSDate(this.deadline);

        return Math.floor(deadline.diffNow("days").days) < 0;
    }

    get variant(): "success" | "warning" | "danger" | "primary" {
        if (this.isCompleted()) return "success";

        if (this.isLate()) return "danger";

        if (this.nearDeadline()) return "warning";

        return "primary";
    }

    intervalBetweenCreationAndDeadline() {
        const createdAt = DateTime.fromJSDate(this.createdAt, {
            zone: "America/Sao_Paulo",
        }).startOf("day");
        const deadline = DateTime.fromJSDate(this.deadline, {
            zone: "America/Sao_Paulo",
        }).startOf("day");

        return deadline.diff(createdAt, "days").days;
    }

    static fromContract(contract: GovPlanSecretaryProjectRoadmapItemContract) {
        return new this({
            id: contract.id,
            amount: contract.amount,
            createdAt: new Date(contract.createdAt),
            deadline: new Date(contract.deadline),
            description: contract.description,
            name: contract.name,
            priority: contract.priority,
            projectId: contract.projectId,
            status: contract.status,
            updatedAt: new Date(contract.updatedAt),
            canceledAt: contract.canceledAt
                ? new Date(contract.canceledAt)
                : undefined,
            completedAt: contract.completedAt
                ? new Date(contract.completedAt)
                : undefined,
            observation: contract.observation,
        });
    }
}
